import React, {useEffect} from "react";
import {Button, Card, Col, DatePicker, Form, Row} from "antd";
import dayjs, {Dayjs} from 'dayjs';

import {
    checkStartTimeBeforeEndTime,
    checkStartTimeFuture,
    dstOffSetDateCheck,
    getBookingIncrement,
} from "../../helpers/booking";
import {getTimeDiff} from "../../helpers/misc";
import {useAppSelector} from "../../hooks/useStore";
import {selectIsStaff} from "../../store/slices/userSlice";
import {PreBookingDetailsCardParams} from "../../types/bookingTypes";

import {formInCardLayout, formItemFullWidth} from "../../assets/styles";
import {dateTimeFormat} from "../../lib/time";
import "./PreBookingDetailsCard.less";

const PreBookingDetailsCard = ({startedAt, setStartedAt, endedAt, setEndedAt, availability, returnType}: PreBookingDetailsCardParams) => {
    const mobileMode = window.innerWidth <= 768;
    const isStaff = useAppSelector(selectIsStaff);
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue({
            'Start Time': startedAt,
            'End Time': endedAt,
        });
    }, [form, startedAt, endedAt]);

    const onStartTimeChange = (newStart: Dayjs | null) => {
        if (newStart) {
            const [start, end] = dstOffSetDateCheck({
                start: newStart,
                end: form.getFieldValue('End Time'),
                isStart: true,
                format: dateTimeFormat,
            });
            form.setFieldsValue({'Start Time': start, 'End Time': end});
        }
    };

    const onEndTimeChange = (newEnd: Dayjs | null) => {
        if (newEnd) {
            const [start, end] = dstOffSetDateCheck({
                start: form.getFieldValue('Start Time'),
                end: newEnd,
                isStart: false,
                format: dateTimeFormat,
            });
            form.setFieldsValue({'Start Time': start, 'End Time': end});
        }
    };

    const resetTimeToOriginal = React.useCallback(() => {
        form.setFieldsValue({
            'Start Time': dayjs(startedAt).tz(),
            'End Time': dayjs(endedAt).tz(),
        });
    }, [startedAt, endedAt, form]);

    const updateBookingTimes = (values: {'Start Time': Dayjs, 'End Time': Dayjs}) => {
        const {"Start Time": startedAt, "End Time": endedAt} = values;

        if (startedAt && endedAt) {
            const currentMinute = dayjs().startOf('minute');

            if (!checkStartTimeBeforeEndTime(startedAt, endedAt)) {
                resetTimeToOriginal();
                return;
            }

            // If not staff, start must be now or in the future.
            if (!isStaff && !checkStartTimeFuture(startedAt, currentMinute)) {
                resetTimeToOriginal();
                return;
            }

            setStartedAt(startedAt);
            setEndedAt(endedAt);
        }
    }

    const disabledDate = (input: dayjs.Dayjs) => {
        return input < dayjs().subtract(1, 'days').endOf('day');
    }

    // Static variables used for render.
    const bookingIncrementMin = Number(getBookingIncrement()) / 60;
    const duration = getTimeDiff(startedAt, endedAt);

    const commonTimePickerProps = {
        showTime: {
            minuteStep: bookingIncrementMin,
            use12Hours: true,
            format: "hh:mm A",
        },
        disabledDate: isStaff ? undefined : disabledDate,
        format: dateTimeFormat,
        allowClear: false,
        inputReadOnly: mobileMode,
        style: {...formItemFullWidth},
    };

    return (
        <Card title="Booking Details" className="pre-booking-details-card">
            <Row>
                <Col md={10} xs={12}><strong>Status:</strong></Col>
                <Col md={14} xs={12} className={availability === 'AVAILABLE' ? 'state-available' : 'state-unavailable'}>{availability.toLowerCase()}</Col>
            </Row>
            <Row>
                <Col md={10} xs={12}><strong>Return Type:</strong></Col>
                <Col md={14} xs={12}>{returnType} Return</Col>
            </Row>
            <Row>
                <Col md={10} xs={12}><strong>Duration:</strong></Col>
                <Col md={14} xs={12}>{duration || 'N/A'}</Col>
            </Row>
            <Form
                {...formInCardLayout}
                className="update-form"
                name="update-booking"
                form={form}
                onFinish={updateBookingTimes}
            >
                <Form.Item name="Start Time" label="Start Date & Time" required rules={[{type: 'object'}]}>
                    <DatePicker
                        {...commonTimePickerProps}
                        onChange={onStartTimeChange}
                    />
                </Form.Item>
                <Form.Item name="End Time" label="End Date & Time" required rules={[{type: 'object'}]}>
                    <DatePicker
                        {...commonTimePickerProps}
                        onChange={onEndTimeChange}
                    />
                </Form.Item>
                <Form.Item className="button-wrapper">
                    <Button type="primary" htmlType="submit">Update Booking</Button>
                </Form.Item>
            </Form>
        </Card>
    );
}

export default PreBookingDetailsCard;
